import store from 'store/Store';
import basketUtils from 'utils/Basket';
import orderUtils from 'utils/Order';
import ErrorsUtils from 'utils/Errors';
import ErrorConstants from 'utils/ErrorConstants';
import userUtils from 'utils/User';
import localeUtils from 'utils/LanguageLocale';
import checkoutApi from 'services/api/checkout';
import locationUtils from 'utils/Location';
import PayPalCheckout from 'utils/PayPalCheckout';
import { showModalError } from 'actions/PayPalActions';

let paypalIntegration;
let paypalToken;
let isTokenRequestInProgress;

const TYPES = {
    ENABLED: 'ENABLED',
    DISABLED: 'DISABLED',
    HIDDEN: 'HIDDEN'
};

const PayPal = {
    TYPES,

    getPayPalPaymentType: function (isBopis) {
        const basketData = store.getState().basket;
        const basket = isBopis ? basketData.pickupBasket : basketData;
        const shippingCountry = userUtils.getShippingCountry().countryCode;
        const isShowPayPal =
            basket.isPaypalPaymentEnabled &&
            (Sephora.isMobile() || shippingCountry === localeUtils.COUNTRIES.US || shippingCountry === localeUtils.COUNTRIES.CA);

        if (isShowPayPal) {
            return basketUtils.isPaypalRestricted(isBopis) ? TYPES.DISABLED : TYPES.ENABLED;
        } else {
            return TYPES.DISABLED;
        }
    },

    preparePaypalCheckout: async function () {
        if (PayPalCheckout.isPayPalCheckoutEnabledForLocale()) {
            try {
                await PayPalCheckout.preparePaypalCheckout();
            } catch (errorData) {
                ErrorsUtils.collectAndValidateBackEndErrors(errorData);
            }

            return;
        }

        // TODO PAYPAL: Remove this LEGACY FLOW block after new flow is served to 100% of users
        if (paypalToken) {
            // Token already exists, just set up
            try {
                await PayPal.paypalSetup(paypalToken);
            } catch (err) {
                Sephora.logger.error('PayPal: paypalSetup failed', { error: err });
            }

            return;
        }

        if (isTokenRequestInProgress) {
            // Token request already in progress, no-op to avoid duplicate requests
            return;
        }

        isTokenRequestInProgress = true;
        try {
            const data = await checkoutApi.getPayPalToken();
            try {
                await PayPal.paypalSetup(data.token);
            } catch (err) {
                Sephora.logger.error('PayPal: paypalSetup failed', { error: err });
            }
        } catch (errorData) {
            ErrorsUtils.collectAndValidateBackEndErrors(errorData);
        } finally {
            isTokenRequestInProgress = false;
        }
    },

    /**
     * Keep this function as plain as possible!
     * No promises, callback chains -> any of this logic could be treated as a
     * suspicious behavior and browser will block the new window with PayPal
     * Explanation of the issue:
     * http://stackoverflow.com/questions/2587677/avoid-browser-popup-blockers
     * NOTE: This function is only used for the legacy PayPal integration.
     * When PayPalCheckoutEnabled is true, the actual PayPal button is rendered
     * directly and this function is not called.
     * TODO PAYPAL: Remove this LEGACY FLOW method after new flow is served to 100% of users
     * @param callback
     */
    showPayPal: function (callback) {
        const isPickupOrder = basketUtils.isPickup();
        const isCheckout = locationUtils.isCheckout();
        const amountWithCurrency = isCheckout
            ? orderUtils.getPayPalAmount() || orderUtils.getCreditCardTotal()
            : basketUtils.getSubtotal(true, isPickupOrder);
        const amount = amountWithCurrency ? Number(basketUtils.removeCurrency(amountWithCurrency)) : 0;
        const { payPalnonVaulting = false } = Sephora.configurationSettings;

        // Because PayPal tokenization opens a popup, this must be called
        // as a result of a user action, such as a button click.
        paypalIntegration.tokenize(
            {
                flow: payPalnonVaulting ? 'checkout' : 'vault',
                billingAgreementDescription: 'Estimated Total: ' + amountWithCurrency,
                container: 'paypal-container',
                singleUse: false,
                amount: amount,
                currency: localeUtils.isCanada() ? 'CAD' : 'USD',
                enableShippingAddress: true,
                enableBillingAddress: true,
                headless: true,
                ...(payPalnonVaulting && { 'enable-funding': 'paylater' }),
                ...(isPickupOrder && { shippingAddressOverride: basketUtils.shippingAddressOverride() })
            },
            (tokenizeErr, payload) => {
                if (tokenizeErr) {
                    switch (tokenizeErr.code) {
                        case ErrorConstants.ERROR_CODES.PAYPAL_ACCOUNT_TOKENIZATION_FAILED:
                            Sephora.logger.error('PayPal tokenization failed. See details:', tokenizeErr.details);
                            // Show authorization error modal (PAY-6417 AC3, AC10)
                            store.dispatch(showModalError({ isAuthorizationFailure: true }));

                            break;
                        case 'PAYPAL_FLOW_FAILED':
                            Sephora.logger.error('Unable to initialize PayPal flow. Are your options correct?', tokenizeErr.details);
                            store.dispatch(showModalError({ isAuthorizationFailure: true }));

                            break;
                        case ErrorConstants.ERROR_CODES.PAYPAL_POPUP_CLOSED:
                            if (!isCheckout) {
                                store.dispatch(showModalError({ isCancellation: true, isAuthorizationFailure: false }));
                            }

                            break;
                        default:
                            Sephora.logger.error('Error!', tokenizeErr);
                            store.dispatch(showModalError());
                    }
                } else {
                    if (typeof callback === 'function') {
                        callback(payload);
                    }
                }
            }
        );
    },

    // TODO PAYPAL: Remove this LEGACY FLOW method after new flow is served to 100% of users
    paypalSetup: async function (clientToken) {
        paypalToken = clientToken;
        const client = await import(/* webpackChunkName: "priority" */ 'BraintreeClient');
        client.create({ authorization: clientToken }, (clientErr, clientInstance) => {
            if (clientErr) {
                Sephora.logger.error('Error creating Braintree Client:', clientErr);

                return;
            }

            import(/* webpackChunkName: "priority" */ 'BraintreePayPal').then(braintreePayPal => {
                braintreePayPal.create({ client: clientInstance }, (paypalErr, paypalInstance) => {
                    if (paypalErr) {
                        Sephora.logger.error('Error creating Braintree PayPal:', paypalErr);

                        return;
                    }

                    paypalIntegration = paypalInstance;
                });
            });
        });
    }
};

export default PayPal;
